/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Ring buffer for the dipole-current samples J(t) = Σᵢ qᵢ vᵢ(t) used by
// the IR-spectrum panel. Three Float32Array components plus a parallel
// time stamp array — typed-array storage keeps a 50-ps trajectory at 2-fs
// stride to about 400 KB and dodges per-sample object allocations in the
// hot integrator loop.
//
// Capacity is fixed at construction. When full, the oldest sample is
// dropped (FIFO ring) so a long-running simulation can stream samples
// indefinitely while the buffer keeps showing the most recent window.
//
// getComponents() returns chronologically ordered components. While the
// buffer hasn't wrapped this is a cheap view into the underlying arrays;
// once wrapped it allocates fresh Float32Arrays and copies into them in
// the right order. That allocation only happens at spectrum-compute
// time (user click), not in the hot loop.
//
// The buffer always holds ONE contiguous, uniformly sampled run: a push whose
// timestamp doesn't continue the established stride (recording resumed after
// a gap, clock reset, time-step change) clears it first — see sampleContinuity.

import { continuesSampleRun, sampleSpacingChanged } from './sampleContinuity';

export class DipoleCurrentBuffer {
  readonly capacity: number;
  private jx: Float32Array;
  private jy: Float32Array;
  private jz: Float32Array;
  // Simulation time in femtoseconds for each sample. Stored explicitly
  // so the spectrum panel can derive the sample stride from observed
  // spacings rather than trusting an external assumption about the MD
  // step — and so wrap-aware durationFs() can report the visible window.
  // Float64: a Float32 stamp has 1 fs resolution past 2²⁴ fs and only
  // represents multiples of 2 fs beyond, so a 1.5 fs spacing turned into
  // alternating 1 / 2 fs gaps after ~8 ns and the guard kept restarting.
  private t: Float64Array;
  private head: number = 0;
  private filled: boolean = false;
  // Sample spacing the current run was recorded at, when the sampler states
  // it (null until a push says). A different stated spacing starts a new run.
  private runSpacingFs: number | null = null;

  constructor(capacity: number) {
    if (capacity < 2) throw new Error(`DipoleCurrentBuffer: capacity must be ≥ 2, got ${capacity}`);
    this.capacity = capacity;
    this.jx = new Float32Array(capacity);
    this.jy = new Float32Array(capacity);
    this.jz = new Float32Array(capacity);
    this.t = new Float64Array(capacity);
  }

  // `spacingFs` (optional) is the sampler's own sample spacing — stride ×
  // timestep. Pass it when known so a timestep change mid-record restarts the
  // tape instead of mixing two spacings (see sampleContinuity).
  push(jx: number, jy: number, jz: number, timeFs: number, spacingFs?: number): void {
    if (
      sampleSpacingChanged(this.runSpacingFs, spacingFs) ||
      !continuesSampleRun(this.t, this.head, this.filled, this.capacity, timeFs)
    )
      this.clear();
    if (spacingFs !== undefined) this.runSpacingFs = spacingFs;
    this.jx[this.head] = jx;
    this.jy[this.head] = jy;
    this.jz[this.head] = jz;
    this.t[this.head] = timeFs;
    this.head = (this.head + 1) % this.capacity;
    if (this.head === 0) this.filled = true;
  }

  clear(): void {
    this.head = 0;
    this.filled = false;
    this.runSpacingFs = null;
  }

  size(): number {
    return this.filled ? this.capacity : this.head;
  }

  isEmpty(): boolean {
    return this.size() === 0;
  }

  // Span of the buffered window in femtoseconds. Zero if fewer than two
  // samples have been pushed.
  durationFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return this.t[last] - this.t[start];
  }

  // Average sample stride in femtoseconds. Equal to durationFs / (N − 1)
  // when sampling is uniform; for a non-uniform stride (adaptive MD)
  // this is the time-averaged Δt, which is what computeSpectrum expects.
  // Returns 0 for buffers with fewer than two samples.
  averageDtFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    return this.durationFs() / (n - 1);
  }

  // Chronologically ordered samples + the averaged Δt. Returns null if
  // there are fewer than two samples. The arrays are views into the
  // backing storage when the buffer hasn't wrapped (cheap); once
  // wrapped, fresh arrays are allocated and the ring is unfolded into
  // them. Callers should treat the returned arrays as read-only since
  // the view path aliases the buffer.
  getComponents(): { jx: Float32Array; jy: Float32Array; jz: Float32Array; dtFs: number } | null {
    const n = this.size();
    if (n < 2) return null;
    let jx: Float32Array, jy: Float32Array, jz: Float32Array;
    if (!this.filled) {
      jx = this.jx.subarray(0, n);
      jy = this.jy.subarray(0, n);
      jz = this.jz.subarray(0, n);
    } else {
      jx = new Float32Array(n);
      jy = new Float32Array(n);
      jz = new Float32Array(n);
      const tail = this.head;
      const firstChunk = n - tail;
      jx.set(this.jx.subarray(tail), 0);
      jx.set(this.jx.subarray(0, tail), firstChunk);
      jy.set(this.jy.subarray(tail), 0);
      jy.set(this.jy.subarray(0, tail), firstChunk);
      jz.set(this.jz.subarray(tail), 0);
      jz.set(this.jz.subarray(0, tail), firstChunk);
    }
    return { jx, jy, jz, dtFs: this.averageDtFs() };
  }

  // Time extent (fs) of the buffered window, for retrospective segment
  // resolution. count is the number of buffered samples.
  windowExtentFs(): { minFs: number; maxFs: number; count: number } {
    const n = this.size();
    if (n === 0) return { minFs: 0, maxFs: 0, count: 0 };
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return { minFs: this.t[start], maxFs: this.t[last], count: n };
  }

  // Chronologically ordered samples whose timestamp lies in [t0Fs, t1Fs], plus
  // the windowed averaged Δt and the actual span. Returns null when fewer than
  // two samples fall inside the window. Used by chat-driven trajectory-SEGMENT
  // analysis to transform just a slice of the recorded dipole trajectory.
  getComponentsInWindow(
    t0Fs: number,
    t1Fs: number,
  ): { jx: Float32Array; jy: Float32Array; jz: Float32Array; dtFs: number; startFs: number; endFs: number } | null {
    const n = this.size();
    if (n < 2) return null;
    const start = this.filled ? this.head : 0;
    const jx: number[] = [];
    const jy: number[] = [];
    const jz: number[] = [];
    let startFs = Infinity;
    let endFs = -Infinity;
    for (let k = 0; k < n; k++) {
      const idx = (start + k) % this.capacity;
      const tk = this.t[idx];
      if (tk < t0Fs || tk > t1Fs) continue;
      jx.push(this.jx[idx]);
      jy.push(this.jy[idx]);
      jz.push(this.jz[idx]);
      if (tk < startFs) startFs = tk;
      if (tk > endFs) endFs = tk;
    }
    if (jx.length < 2) return null;
    const dtFs = (endFs - startFs) / (jx.length - 1);
    return { jx: Float32Array.from(jx), jy: Float32Array.from(jy), jz: Float32Array.from(jz), dtFs, startFs, endFs };
  }
}
